import Alert from '@mui/material/Alert';
import OutlinedInput from '@mui/material/OutlinedInput';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';
import { patchAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

const inputStyle = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

type DataType = {
  barcode: string;
  patient_name: string;
  patient_number: string;
  creation_date: string;
  expiry_date: string;
  storage_date: string;
  total_quantity: number;
  status: string;
  used_quantity: number;
  used_log: any[];
};

const DEFAULT_DATA = {
  barcode: '',
  patient_name: '',
  patient_number: '',
  creation_date: '',
  expiry_date: '',
  storage_date: '',
  total_quantity: 0,
  status: '',
  used_quantity: 0,
  used_log: [],
};

const DialogScanBarcodeConfirm = (props: any) => {
  const notifyContext = useNotify();
  const [patientInfo, setPatientInfo] = useState<DataType>(DEFAULT_DATA);
  const [useCount, setUseCount] = useState('1');
  const [error, setError] = useState('');

  useEffect(() => {
    if (props.inventoryScanBarcode) {
      setPatientInfo(props.inventoryScanBarcode);
    }
  }, [props.inventoryScanBarcode]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setUseCount(value);
      setError('');

      const numValue = parseInt(value, 10);
      if (numValue > patientInfo.total_quantity - patientInfo.used_quantity) {
        setError('在庫数を超える数は入力できません');
      } else if (numValue <= 0) {
        setError('1以上の数を入力してください');
      }
    }
  };

  const handleConfirm = () => {
    const numValue = parseInt(useCount, 10);
    if (!useCount || numValue <= 0) {
      setError('1以上の数を入力してください');
      return;
    }
    if (numValue > patientInfo.total_quantity - patientInfo.used_quantity) {
      setError('在庫数を超える数は入力できません');
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      patchAPI(`/inventory/${patientInfo.barcode}/usedlog?used_count=${useCount}?token=${token}`)
        .then((res) => {
          if (res.code === 200) {
            console.log(res.data);
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {}
    // onConfirm(numValue);
  };

  const handleClose = () => {
    setUseCount('1');
    setError('');
    props.handleClose()
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='scroll-dialog-title'>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography fontSize={'20px'} fontWeight={500}>
            使用確認
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '500px' }}>
        <Stack spacing={'8px'}>
          <Stack
            direction={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
          >
            <Typography color={'#00000080'} width={'100px'}>
              患者ID
            </Typography>
            <Typography>{patientInfo?.patient_number}</Typography>
          </Stack>
          <Stack
            direction={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
          >
            <Typography color={'#00000080'} width={'100px'}>
              患者名
            </Typography>
            <Typography>{patientInfo?.patient_name}</Typography>
          </Stack>
          <Stack
            direction={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
          >
            <Typography color={'#00000080'} width={'100px'}>
              製造日
            </Typography>
            <Typography>{patientInfo?.creation_date}</Typography>
          </Stack>
          <Stack
            direction={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
          >
            <Typography color={'#00000080'} width={'100px'}>
              保管期限
            </Typography>
            <Typography>{patientInfo?.expiry_date}</Typography>
          </Stack>
          <Stack
            direction={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
          >
            <Typography color={'#00000080'} width={'100px'}>
              消費期限
            </Typography>
            <Typography>{patientInfo?.storage_date}</Typography>
          </Stack>
          <Stack
            direction={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
          >
            <Typography color={'#00000080'} width={'100px'}>
              使用数
            </Typography>
            <OutlinedInput
              placeholder={'入力してください'}
              sx={inputStyle}
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              type='number'
              name='total_quantity'
              value={useCount}
              onChange={(event) => handleInputChange(event)}
            />
          </Stack>
          <Stack direction={'row'} justifyContent={'flex-start'}>
            {error && (
              <Alert severity='error' sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '20px' }}>
        <Typography sx={{ flexGrow: 1, ml: 2 }}>使用しますか？</Typography>
        <BtnOutlineGradient text='いいえ' handleButtonClick={handleClose} />
        <BtnGradient
          text='はい'
          handleButtonClick={handleConfirm}
          disabled={!!error || !useCount}
        />
      </DialogActions>
    </Dialog>
  );
};

export default DialogScanBarcodeConfirm;
